import { useQuery } from '@tanstack/react-query'
import { itemInstanceApi } from '@/lib/api'
import type { EvidenceItem } from '@/lib/api'
import { cn } from '@/lib/utils'
import { statusInfo } from './reportHelpers'
import { PhotoPlate } from './PhotoPlate'
import { EditableNote } from './EditableNote'
import { Loader2, MessageSquare, File } from 'lucide-react'

export function AreaSection({ area, sectionNumber, evidence, noteValue, onNoteChange }: {
  area: Record<string, unknown>
  sectionNumber: string
  evidence: EvidenceItem[]
  noteValue: string
  onNoteChange: (key: string, val: string) => void
}) {
  const areaId = area.id as string
  const isLocked = area.status === 'locked' || area.is_locked

  const { data: itemsData, isLoading } = useQuery({
    queryKey: ['qa-items-for-report', areaId],
    queryFn: () => itemInstanceApi.getItems({ area_id: areaId, page_size: 200 }),
    staleTime: 30_000,
  })

  const items = Array.isArray(itemsData?.results) ? itemsData.results : []
  const photos = evidence.filter(e => (e.type === 'photo' || e.type === 'video') && e.asset_url)
  const notes = evidence.filter(e => e.type === 'note')
  const docs = evidence.filter(e => e.type === 'document')
  const doneCount = items.filter((i: Record<string, unknown>) => ['done', 'DONE'].includes(i.status as string)).length
  const failCount = items.filter((i: Record<string, unknown>) => ['failed', 'FAILED'].includes(i.status as string)).length

  return (
    <div className="mb-6">
      <div className="flex items-baseline gap-3 mb-3">
        <span className="text-base font-bold text-slate-800">{sectionNumber}</span>
        <div className="flex-1">
          <span className="text-base font-bold text-slate-800">{area.area_name as string}</span>
          <span className="text-sm text-slate-500 ml-2">({area.area_code as string})</span>
        </div>
        <span className={cn(
          'text-[10px] font-bold uppercase px-2 py-0.5 rounded border',
          isLocked ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-600 border-slate-200'
        )}>
          {isLocked ? 'COMPLETE' : (area.status as string || '').replace(/_/g, ' ').toUpperCase()}
        </span>
      </div>

      {items.length > 0 && (
        <p className="text-xs text-slate-600 mb-2">
          <strong>{doneCount}</strong> of <strong>{items.length}</strong> items passed
          {failCount > 0 && <span className="text-red-600 font-semibold ml-1">({failCount} failed)</span>}
        </p>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
      ) : items.length > 0 ? (
        <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-slate-100">
              <th className="text-left px-3 py-2 text-[10px] font-bold text-slate-600 uppercase w-8 border-b border-slate-200">#</th>
              <th className="text-left px-3 py-2 text-[10px] font-bold text-slate-600 uppercase border-b border-slate-200">Inspection Item</th>
              <th className="text-center px-3 py-2 text-[10px] font-bold text-slate-600 uppercase w-20 border-b border-slate-200">Result</th>
              <th className="text-left px-3 py-2 text-[10px] font-bold text-slate-600 uppercase w-28 border-b border-slate-200">Inspector</th>
              <th className="text-left px-3 py-2 text-[10px] font-bold text-slate-600 uppercase w-24 border-b border-slate-200">Date</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item: Record<string, unknown>, idx: number) => {
              const si = statusInfo(item.status as string)
              return (
                <tr key={item.id as string} className={cn('border-b border-slate-100', idx % 2 === 1 && 'bg-slate-50/50')}>
                  <td className="px-3 py-2 text-xs text-slate-500 font-mono">{idx + 1}</td>
                  <td className="px-3 py-2">
                    <span className="text-sm text-slate-800">{item.title as string || item.item_name as string || item.template_item_name as string || `Item ${idx + 1}`}</span>
                    {item.override_reason && (
                      <span className="block text-[10px] text-amber-600 mt-0.5">Override: {item.override_reason as string}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={cn('inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded border', si.bg, si.color)}>
                      {si.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{item.completed_by_name as string || '—'}</td>
                  <td className="px-3 py-2 text-xs text-slate-500 font-mono">
                    {item.completed_at ? new Date(item.completed_at as string).toLocaleDateString('en-AU') : '—'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      ) : (
        <p className="text-xs text-slate-500 italic py-2">No checklist items defined for this area.</p>
      )}

      <PhotoPlate photos={photos} />

      {notes.length > 0 && (
        <div className="mt-4">
          <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2 flex items-center gap-1.5 border-b border-slate-200 pb-1.5">
            <MessageSquare className="w-3.5 h-3.5" /> Field Notes ({notes.length})
          </h4>
          <div className="space-y-2">
            {notes.map(n => (
              <div key={n.id} className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
                <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{n.note_text}</p>
                <p className="text-[10px] text-slate-500 mt-1.5 font-medium">
                  {n.captured_by_name} — {new Date(n.captured_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {docs.length > 0 && (
        <div className="mt-4">
          <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2 flex items-center gap-1.5 border-b border-slate-200 pb-1.5">
            <File className="w-3.5 h-3.5" /> Attached Documents ({docs.length})
          </h4>
          <div className="space-y-1">
            {docs.map(d => (
              <div key={d.id} className="flex items-center gap-2 py-1.5 px-3 rounded bg-amber-50 border border-amber-100">
                <File className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                <span className="text-sm text-slate-800 flex-1 truncate">{d.description || 'Document'}</span>
                <span className="text-[10px] text-slate-500">{new Date(d.captured_at).toLocaleDateString('en-AU')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <EditableNote noteKey={`area_${areaId}_note`} value={noteValue} onChange={onNoteChange} placeholder="Add an inspector note for this area..." />
    </div>
  )
}
